import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Check, Star } from "lucide-react";
import { WhatsAppIcon } from "./Reveal";
import { LiveStats } from "./LiveStats";
import { BACKDROPS, POSTERS, wa, WA_MSG, scrollToSection } from "../constants/site";

const lineEase = [0.16, 1, 0.3, 1];

const RevealLine = ({ children, index }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.15 + index * 0.13, duration: 0.9, ease: lineEase }}
        >
            {children}
        </motion.span>
    </span>
);

const wallRowA = [
    POSTERS.endgame,
    POSTERS.avatar,
    POSTERS.batman,
    POSTERS.johnwick,
    POSTERS.breakingbad,
    POSTERS.topgun,
    POSTERS.lastofus,
    POSTERS.guardians,
];
const wallRowB = [
    POSTERS.spiderman,
    POSTERS.darkknight,
    POSTERS.round6,
    POSTERS.flash,
    POSTERS.panther,
    POSTERS.lacasadepapel,
    POSTERS.got,
    POSTERS.peaky,
];

const WallRow = ({ images, reverse = false, duration = 80 }) => (
    <div
        className="marquee-track flex w-max gap-4 animate-marquee"
        style={{
            animationDuration: `${duration}s`,
            animationDirection: reverse ? "reverse" : "normal",
        }}
    >
        {[0, 1].map((copy) => (
            <div key={copy} aria-hidden={copy === 1} className="flex w-max gap-4 pr-4">
                {images.map((src, i) => (
                    <img
                        key={i}
                        src={src}
                        alt=""
                        loading={copy === 0 ? "eager" : "lazy"}
                        className="aspect-[2/3] h-32 rounded-lg object-cover md:h-44"
                    />
                ))}
            </div>
        ))}
    </div>
);

const miniPosters = [POSTERS.endgame, POSTERS.spiderman, POSTERS.darkknight, POSTERS.johnwick];

export const Hero = () => {
    const ref = useRef(null);
    const rotateX = useSpring(0, { stiffness: 120, damping: 16 });
    const rotateY = useSpring(0, { stiffness: 120, damping: 16 });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const chipY1 = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const chipY2 = useTransform(scrollYProgress, [0, 1], [0, 50]);

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        rotateY.set(px * 8);
        rotateX.set(-py * 8);
    };

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={() => {
                rotateX.set(0);
                rotateY.set(0);
            }}
            className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24"
        >
            {/* Movie poster wall */}
            <div aria-hidden className="absolute inset-0">
                <div className="absolute -inset-x-16 -top-28 flex -rotate-6 flex-col gap-5 opacity-45 blur-[1px]">
                    <WallRow images={wallRowA} duration={80} />
                    <WallRow images={wallRowB} reverse duration={100} />
                    <WallRow images={[...wallRowA].reverse()} duration={90} />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-[#070709] via-[#070709]/60 to-[#070709]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#070709] via-[#070709]/45 to-[#070709]/15" />
                <div className="grain absolute inset-0" />
            </div>

            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-8 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: lineEase }}
                    >
                        <span className="inline-flex items-center gap-2 rounded-full border border-[#E50914]/35 bg-[#E50914]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#FF4757]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#FF1A25] animate-pulse-dot" />
                            IPTV Premium • 4K • Zero travamentos
                        </span>
                    </motion.div>

                    <h1
                        aria-label="Cinema, séries e futebol ao vivo num só lugar."
                        className="mt-6 text-4xl font-extrabold uppercase leading-[1.04] text-white sm:text-5xl lg:text-6xl"
                    >
                        <RevealLine index={0}>Cinema, séries</RevealLine>
                        <RevealLine index={1}>
                            <span className="glow-red text-[#E50914]">
                                e futebol ao vivo
                            </span>
                        </RevealLine>
                        <RevealLine index={2}>num só lugar.</RevealLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.7, ease: lineEase }}
                        className="mt-6 max-w-lg text-base leading-relaxed text-[#94A3B8] md:text-lg"
                    >
                        De Vingadores a Breaking Bad, do jogo do seu time ao
                        último lançamento do cinema — tudo em 4K, sem anúncios e
                        sem travar, direto na sua Smart TV, celular ou TV Box.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.7, ease: lineEase }}
                        className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
                    >
                        <a
                            href={wa(WA_MSG.trial)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-btn"
                            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-base font-bold text-white shadow-xl shadow-[#25D366]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1fb857]"
                        >
                            <WhatsAppIcon className="h-5 w-5" />
                            Testar grátis no WhatsApp
                        </a>
                        <a
                            href="#planos"
                            data-testid="hero-plans-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("#planos");
                            }}
                            className="group inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/70 px-7 py-4 text-base font-bold text-white transition-all duration-300 hover:border-[#E50914] hover:bg-[#E50914]"
                        >
                            Ver planos
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1, duration: 0.8 }}
                        className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-[#94A3B8]"
                    >
                        {["Sem fidelidade", "Ativação em minutos", "Suporte 24h"].map(
                            (item) => (
                                <li key={item} className="flex items-center gap-1.5">
                                    <Check className="h-4 w-4 text-[#25D366]" />
                                    {item}
                                </li>
                            ),
                        )}
                    </motion.ul>

                    <div className="mt-6">
                        <LiveStats />
                    </div>
                </div>

                <div className="relative hidden lg:col-span-6 lg:block">
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.5, duration: 1, ease: lineEase }}
                        style={{
                            rotateX,
                            rotateY,
                            transformPerspective: 1100,
                            transformStyle: "preserve-3d",
                        }}
                        className="relative mx-auto max-w-xl"
                        data-testid="hero-tv-mockup"
                    >
                        <div className="rounded-[1.75rem] border border-[#222232] bg-[#0E0E14]/85 p-3 shadow-2xl shadow-black/60 backdrop-blur">
                            <div className="overflow-hidden rounded-2xl bg-black">
                                <div className="flex items-center justify-between px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#E50914]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FFB800]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#25D366]" />
                                    </div>
                                    <span className="font-display text-xs font-bold tracking-widest text-white/70">
                                        DRAX PLAYER
                                    </span>
                                    <span className="flex items-center gap-1.5 rounded-full bg-[#E50914] px-2.5 py-0.5 text-[10px] font-extrabold text-white">
                                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse-dot" />
                                        AO VIVO
                                    </span>
                                </div>

                                <div className="relative aspect-video">
                                    <img
                                        src={BACKDROPS.batman}
                                        alt="The Batman em exibição na Drax Filmes"
                                        className="h-full w-full object-cover"
                                        loading="eager"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                                        <div>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-extrabold text-[#070709]">
                                                <Star className="h-3 w-3 text-[#FFB800]" fill="currentColor" />
                                                7.8
                                            </span>
                                            <p className="mt-1.5 font-display text-lg font-bold text-white">
                                                The Batman
                                            </p>
                                        </div>
                                        <span className="rounded-lg bg-[#00F0FF] px-2 py-1 text-[10px] font-extrabold text-[#070709]">
                                            4K 60FPS
                                        </span>
                                    </div>
                                    <div className="absolute bottom-0 left-0 h-1 w-2/5 bg-[#E50914]" />
                                </div>

                                <div className="grid grid-cols-4 gap-2 p-3">
                                    {miniPosters.map((src, i) => (
                                        <div
                                            key={i}
                                            className="relative aspect-[2/3] overflow-hidden rounded-lg opacity-80 transition-opacity hover:opacity-100"
                                        >
                                            <img
                                                src={src}
                                                alt="Pôster em cartaz na Drax Filmes"
                                                className="h-full w-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <motion.div
                            style={{ y: chipY1 }}
                            className="absolute -left-4 -top-5 hidden items-center gap-2 rounded-2xl border border-[#222232] bg-[#0E0E14]/95 px-4 py-3 shadow-xl shadow-black/50 backdrop-blur xl:flex"
                        >
                            <span className="h-2.5 w-2.5 rounded-full bg-[#00F0FF] animate-pulse-dot" />
                            <div>
                                <p className="text-xs font-bold text-white">4K Ultra HD</p>
                                <p className="text-[11px] text-[#64748B]">Imagem de cinema</p>
                            </div>
                        </motion.div>

                        <motion.div
                            style={{ y: chipY2 }}
                            className="absolute -bottom-6 -right-3 hidden items-center gap-2 rounded-2xl border border-[#222232] bg-[#0E0E14]/95 px-4 py-3 shadow-xl shadow-black/50 backdrop-blur xl:flex"
                        >
                            <span className="h-2.5 w-2.5 rounded-full bg-[#25D366] animate-pulse-dot" />
                            <div>
                                <p className="text-xs font-bold text-white">Canais ao vivo</p>
                                <p className="text-[11px] text-[#64748B]">Sem travamentos</p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
